import { errorToast } from "@/v2-ui/toast"
import {
  type ConfirmButtonProps,
  ConfirmButton,
} from "@/v2-backoffice-client/components/button"
import { useLayerDriftAcceptMutation } from "@/v2-backoffice-client/layer/drift/accept/layer.drift.accept.mutation"

export type LayerDriftAcceptButtonProps = Omit<
  ConfirmButtonProps,
  "onConfirm"
> & {
  source: string
  identifier: string
  /**
   * Breaking entries whose tokens the layer's documents still reference.
   * Turns accepting from the obvious first move into a deliberate
   * override.
   */
  unresolvedCount?: number
  onCompleted?(): void
}

/**
 * Promotes the live fingerprint to be the new baseline.
 *
 * Worth being clear about what this does and does not do: it records that
 * the upstream service's current shape is the expected one. It changes
 * nothing about the layer. So while the layer's own documents still name
 * a field the service dropped, accepting doesn't resolve the drift — it
 * hides it, and the board will report the layer as in sync while it is
 * quietly broken. Hence the unresolved-breaking gate below.
 *
 * Available in every environment, unlike the layer's own documents. A
 * baseline is not authored content: it is an observation of what the
 * upstream service returned, it lives in this environment's
 * backoffice.LayerMetaFingerprints, and nothing copies or republishes
 * it between environments. Hotfix mode therefore does not apply, there
 * being no catalog publish that could overwrite it, and gating this on
 * the authoring deployment only meant prod drift could never be
 * cleared at all (FUS-1181). LayerCountDriftAcceptButton has always
 * worked everywhere, for the same reason.
 */
function LayerDriftAcceptButton(props: LayerDriftAcceptButtonProps) {
  const {
    source,
    identifier,
    unresolvedCount = 0,
    onCompleted,
    children,
    ...rest
  } = props

  const [acceptBaseline, { loading, error }] = useLayerDriftAcceptMutation({
    onCompleted,
  })

  errorToast(error, {
    title: "Failed to accept current fingerprint as the new baseline",
  })

  const hasUnresolved = unresolvedCount > 0

  return (
    <ConfirmButton
      variant={hasUnresolved ? "secondary" : "primary"}
      iconSrc={hasUnresolved ? "triangle-warning" : "check"}
      content={
        hasUnresolved
          ? "Record the current upstream shape as expected without adapting the layer to it"
          : "Accept current as baseline"
      }
      confirmContent={
        hasUnresolved
          ? `${unresolvedCount} breaking ${
              unresolvedCount === 1 ? "change is" : "changes are"
            } still referenced by this layer's own documents. Accepting records the new upstream shape as expected and clears the drift indicator — it does not fix those references, and nothing will flag them again. Only do this if the layer genuinely needs no change.`
          : "This replaces the stored baseline with the live fingerprint. The drift indicator will clear until the layer server's metadata changes again."
      }
      confirmVariant={hasUnresolved ? "destructive" : undefined}
      isLoading={loading}
      data-feature-code="UI_BackofficeLayers_Drift_Accept"
      onConfirm={() => {
        acceptBaseline({
          variables: { input: { source, identifier } },
        })
      }}
      {...rest}
    >
      {children ?? (hasUnresolved ? "Accept anyway" : "Accept as baseline")}
    </ConfirmButton>
  )
}

export default LayerDriftAcceptButton
